import { InventoryHoldArea, SHELVING_DEFAULT_FORM_VALUES } from "../../constants"
import type { InventoryDestinationFormValues } from "../../schemas"
import { formatInventoryLocation, isLotLocked } from "../../utils"

import { HOLD_AREA_TO_DESTINATION_AREA } from "./constants"
import type { TransferFormValues } from "./schemas"
import type {
  TransferFormPallet,
  TransferFormProductInventory,
  TransferPalletWithProduct,
  TransferTotals,
} from "./types"

import {
  type CreateTransferInput,
  type InventoryPalletGroup,
  InventoryStatus,
  type Product,
  type Transfer,
  TransferAs400SyncStatus,
  type TransferPallet,
  type TransferProduct,
  type TransferProductInput,
  TransferStatus,
  type UpdateTransferInput,
} from "@/graphql/codegen/graphql"
import { roundToPrecision } from "@/utils/number"
import type { PalletLabelParams } from "@/utils/printing/generatePalletLabelZpl"
import { isNil } from "@/utils/utils"

export const isPalletLocked = (pallet: InventoryPalletGroup): boolean => pallet.lots.some(isLotLocked)

export const calculateProductQtyToMove = (pallets: TransferFormPallet[]): number =>
  pallets.reduce((sum, pallet) => sum + pallet.lots.reduce((lotSum, lot) => lotSum + (lot.totalUnits ?? 0), 0), 0)

const formatDestinationShelving = (
  shelving: { aisleCode?: string | null; sectionCode?: string | null; binNumber?: number | null } | null | undefined
): string => {
  if (!shelving) return ""

  const { aisleCode, sectionCode, binNumber } = shelving
  if (!aisleCode || !sectionCode || isNil(binNumber)) return ""

  return `${aisleCode} / ${sectionCode} / ${binNumber}`
}

export const formatPalletFormDestination = (destination: InventoryDestinationFormValues): string =>
  destination.holdArea !== InventoryHoldArea.Warehouse
    ? formatInventoryLocation(destination.holdArea)
    : formatDestinationShelving(destination.shelving)

export const formatPalletDestination = (pallet: TransferPallet | null | undefined): string =>
  pallet?.destinationWarehouseShelving
    ? formatDestinationShelving(pallet.destinationWarehouseShelving)
    : formatInventoryLocation(pallet?.destinationLocation)

type GetSelectedShelvingIdsParams =
  | { inventories: TransferFormProductInventory[]; productId: number }
  | { inventories: TransferFormProductInventory[]; palletReferenceNumber: number }

export const getSelectedShelvingIds = (params: GetSelectedShelvingIdsParams): string[] =>
  params.inventories
    .flatMap((inventory) => inventory.pallets.map((pallet) => ({ ...pallet, productId: inventory.product.productId })))
    .filter((pallet) =>
      // With a pallet given, exclude just it (siblings of the same product stay in the result); otherwise exclude
      // the whole product, since that caller tracks its own product's pallets separately via live form state.
      "palletReferenceNumber" in params
        ? pallet.referenceNbr !== params.palletReferenceNumber
        : pallet.productId !== params.productId
    )
    .map((pallet) => pallet.destination.shelving.warehouseShelvingId)
    .filter((warehouseShelvingId) => warehouseShelvingId !== null)

const getTotalsUomName = (items: { product: Product }[]): string | null => {
  if (!items.length) return null

  const firstUom = items[0].product.packageUsWeightUom
  const hasSameUom = !isNil(firstUom) && items.every(({ product }) => product.packageUsWeightUom === firstUom)

  return hasSameUom ? (items[0].product.packageUsWeightUomDetails?.name ?? null) : null
}

export const calculateTransferFormTotals = (inventories: TransferFormProductInventory[]): TransferTotals => ({
  ...inventories.reduce(
    (totals, inventory) => ({
      palletCount: totals.palletCount + inventory.pallets.length,
      quantity: totals.quantity + calculateProductQtyToMove(inventory.pallets),
    }),
    { palletCount: 0, quantity: 0 }
  ),
  uomName: getTotalsUomName(inventories),
})

export const calculateTransferTotals = (transferProducts: TransferProduct[]): TransferTotals => ({
  ...transferProducts.reduce(
    (totals, transferProduct) => ({
      palletCount: totals.palletCount + transferProduct.pallets.length,
      quantity:
        totals.quantity +
        transferProduct.pallets.reduce(
          (palletSum, pallet) => palletSum + pallet.lots.reduce((lotSum, lot) => lotSum + (lot.qtyToMove ?? 0), 0),
          0
        ),
    }),
    { palletCount: 0, quantity: 0 }
  ),
  uomName: getTotalsUomName(transferProducts),
})

export const toTransferProductInputs = (inventories: TransferFormProductInventory[]): TransferProductInput[] =>
  inventories.map((inventory) => ({
    productId: inventory.product.productId,
    lots: inventory.pallets.flatMap((pallet) =>
      pallet.lots.map((lot) => ({
        rawInventoryId: lot.rawInventoryId,
        destination:
          pallet.destination.holdArea === InventoryHoldArea.Warehouse
            ? { destinationWarehouseShelvingId: pallet.destination.shelving.warehouseShelvingId }
            : { destinationAreaCode: HOLD_AREA_TO_DESTINATION_AREA[pallet.destination.holdArea] },
      }))
    ),
  }))

export const toTransferCreateInput = (
  formValues: TransferFormValues,
  inventories: TransferFormProductInventory[],
  isDraft: boolean
): CreateTransferInput => ({
  products: toTransferProductInputs(inventories),
  reason: formValues.reason,
  sourceWarehouseAreaId: formValues.sourceWarehouseAreaId,
  isDraft,
  transferType: formValues.type,
})

export const toTransferUpdateInput = (
  formValues: TransferFormValues,
  inventories: TransferFormProductInventory[],
  transferId: string,
  isDraft: boolean
): UpdateTransferInput => ({
  products: toTransferProductInputs(inventories),
  reason: formValues.reason,
  isDraft,
  transferId,
})

export const toTransferFormValues = (transfer: Transfer): TransferFormValues => ({
  type: transfer.transferType,
  sourceWarehouseAreaId: transfer.sourceWarehouseAreaId,
  reason: transfer.reason ?? undefined,
  comment: "",
  documents: [],
})

// A transfer (and its documents) can only be modified while Draft or Pending Pick and Putaway —
// matches the backend's updateTransfer guard. `transfer` is undefined for a not-yet-created
// transfer, which is always modifiable.
export const canModifyTransfer = (transfer?: Transfer): boolean =>
  !transfer || [TransferStatus.Draft, TransferStatus.PendingPickAndPutaway].includes(transfer.status)

// AK-5563 AC5: saving a transfer as non-draft moves it straight to Pending Pick and Putaway before
// AS400 ever responds, so status alone can't tell whether a work order actually exists in AS400 yet.
// While as400SyncStatus is WorkOrderUpserting or WorkOrderUpsertFailed, the initial creation hasn't
// succeeded, so nothing that depends on the work order (Confirm/Print Work Order, Print Pallet Label)
// should be available.
export const isTransferWorkOrderCreated = (transfer: Transfer): boolean =>
  [TransferStatus.PendingPickAndPutaway, TransferStatus.Completed].includes(transfer.status) &&
  !(
    transfer.as400SyncStatus &&
    [TransferAs400SyncStatus.WorkOrderUpserting, TransferAs400SyncStatus.WorkOrderUpsertFailed].includes(
      transfer.as400SyncStatus
    )
  )

const toDestinationFormValues = (pallet: TransferPallet): InventoryDestinationFormValues =>
  pallet.destinationWarehouseShelvingId
    ? {
        holdArea: InventoryHoldArea.Warehouse,
        shelving: {
          warehouseShelvingId: pallet.destinationWarehouseShelvingId,
          aisleCode: pallet.destinationWarehouseShelving?.aisleCode ?? null,
          sectionCode: pallet.destinationWarehouseShelving?.sectionCode ?? null,
          binNumber: pallet.destinationWarehouseShelving?.binNumber ?? null,
          binSize: pallet.destinationWarehouseShelving?.binSize ?? null,
        },
      }
    : {
        holdArea: (pallet.destinationLocation as InventoryHoldArea) ?? InventoryHoldArea.Warehouse,
        shelving: SHELVING_DEFAULT_FORM_VALUES,
      }

export const toTransferFormProductInventories = (transfer: Transfer): TransferFormProductInventory[] =>
  transfer.products.map((transferProduct) => ({
    product: transferProduct.product,
    qtyOnHand: transferProduct.qtyOnHand,
    availableQty: 0,
    balToCome: 0,
    hardAllocatedQty: 0,
    inventoryStatus: InventoryStatus.Good,
    moh: 0,
    mohInStock: 0,
    onHoldQty: 0,
    softAllocatedQty: 0,
    warehouses: [],
    pallets: transferProduct.pallets.map((pallet) => ({
      referenceNbr: pallet.referenceNbr,
      location: pallet.lots[0]?.rawInventory?.location ?? "",
      totalQoh: pallet.lots.reduce((sum, lot) => sum + (lot.rawInventory?.totalUnits ?? 0), 0),
      lots: pallet.lots.map((lot) => ({
        rawInventoryId: lot.rawInventoryId!,
        lotNbr: lot.rawInventory?.lotNbr,
        batchNbr: lot.rawInventory?.batchNbr,
        totalUnits: lot.qtyToMove ?? lot.rawInventory?.totalUnits,
        totalPackages: lot.rawInventory?.totalPackages,
        uom: lot.rawInventory?.uom,
        isLocked: false,
        isLockedByOpenGr: false,
        hasProductTest: true,
      })),
      destination: toDestinationFormValues(pallet),
    })),
  }))

export const toTransferPalletLabelParams = ({
  product,
  referenceNbr,
  lots,
}: TransferPalletWithProduct): PalletLabelParams => {
  const countByPackageQuantity = new Map<number, number>()
  let mostCommonPackageQuantity: number | null = null
  let highestPackageQuantityCount = 0
  let palletQuantity = 0
  let packageCount = 0

  for (const { rawInventory } of lots) {
    const totalUnits = rawInventory?.totalUnits ?? 0
    const totalPackages = rawInventory?.totalPackages ?? 0

    palletQuantity += totalUnits

    if (!totalPackages) continue

    packageCount += totalPackages

    // Round before grouping, so floating-point noise doesn't split a shared package size into separate keys.
    const packageQuantity = roundToPrecision(totalUnits / totalPackages, 2)
    const count = (countByPackageQuantity.get(packageQuantity) ?? 0) + 1
    countByPackageQuantity.set(packageQuantity, count)

    if (count > highestPackageQuantityCount) {
      highestPackageQuantityCount = count
      mostCommonPackageQuantity = packageQuantity
    }
  }

  return {
    productName: product.companyProductDescription ?? "Unknown Product",
    referenceId: String(referenceNbr),
    stockNumber: product.publicId ?? "N/A",
    palletQuantity: String(roundToPrecision(palletQuantity, 2)),
    packageQuantity: String(mostCommonPackageQuantity ?? "N/A"),
    packageCount: Math.round(packageCount),
    lots: lots.map(({ rawInventory }) => ({
      lotNumber: rawInventory?.lotNbr,
      batchNumber: rawInventory?.batchNbr,
      quantity: rawInventory?.totalUnits,
    })),
    uomName: product.packageUsWeightUomDetails?.name ?? "N/A",
  }
}
